import { useTranslation } from "react-i18next";

import { GamesPill } from "@/components/campaigns/games-pill";
import { cn } from "@/lib/utils";

/** Names spelled out where there is no dialog, before the rest collapse into "+N". */
const NAMED = 3;

export interface PrizeGamesChipsProps {
  /** The prize's games (may be a preview subset of `count`). */
  games: string[];
  /** Total game count when `games` is a preview subset. */
  count?: number;
  /** The line's own layout in its parent (alignment, padding). */
  className?: string;
  /** The line centred on its own, under a centred prize (the mission rungs). */
  centered?: boolean;
  /**
   * Whether 2+ games open the searchable games dialog. Off in the prize reveal / tally: that
   * window is a one-shot celebration with a single call to action (confirm), so the games are
   * spelled out there instead of being a second thing to tap.
   */
  browsableRemainder?: boolean;
}

/**
 * The games a single prize applies to (free spins) — ONE treatment on every surface (tournament
 * prize rows, prize-drop rows, mission and Sprint rewards, the reveal), under the prize so the
 * prize always leads:
 * - one game: its name (nothing more to see, so not tappable);
 * - 2+ games: the "N games ›" pill (`GamesPill`), opening the full list titled "Free spins games";
 * - no dialog (reveal / tally): the first names and "+N", so the player still learns which games.
 * Prize titles never truncate; game names here may.
 */
export const PrizeGamesChips = ({
  games,
  count,
  className,
  centered = false,
  browsableRemainder = true,
}: PrizeGamesChipsProps) => {
  const { t } = useTranslation();
  if (games.length === 0) return null;

  const total = count ?? games.length;
  const rest = total - Math.min(NAMED, games.length);

  const body =
    total === 1 ? (
      <span className="min-w-0 truncate">{games[0]}</span>
    ) : browsableRemainder ? (
      // stopPropagation: on a mission rung the line sits inside the rung's own toggle.
      <span className="flex min-w-0" onClick={(e) => e.stopPropagation()}>
        <GamesPill
          games={games}
          count={total}
          title={t("eligibleGames.freeSpinsTitle", "Free spins games")}
        />
      </span>
    ) : (
      <>
        {/* No dialog here: say which games. Only the names clamp; the "+N" always shows. */}
        <span className="line-clamp-2 min-w-0 text-left">
          {games.slice(0, NAMED).join(", ")}
        </span>
        {rest > 0 && (
          <b className="shrink-0 font-bold">+{rest.toLocaleString()}</b>
        )}
      </>
    );

  return (
    <div
      className={cn("flex min-w-0", centered && "justify-center", className)}
    >
      <span className="flex max-w-full min-w-0 items-center gap-2 font-heading text-[11px] leading-4 font-semibold text-foreground-secondary lg:text-[12px]">
        {body}
      </span>
    </div>
  );
};
